import { Button } from '@nextui-org/react'
import {
  type AdvancedMarkerAnchorPoint,
  APIProvider,
  InfoWindow,
  Map,
} from '@vis.gl/react-google-maps'
import _ from 'lodash'
import { useCallback, useState } from 'react'

import AdvancedMarkerWithRef from './components/AdvancedMarkerWithRef'
import { MapSvg } from './components/MapSvg'
import { MAP_API_KEY } from './lib/constant'
import { type StoreLocator, useStoreLocator } from './lib/useStoreLocator'
import { openDirections } from './lib/util'
import StoreLocatorList from './StoreLocatorList'

export type AnchorPointName = keyof typeof AdvancedMarkerAnchorPoint

export function StoreLocatorMap() {
  const { data, isLoading } = useStoreLocator().getAll()

  const [selectedId, setSelectedId] = useState<null | string>(null)
  const [selectedMarker, setSelectedMarker] =
    useState<google.maps.marker.AdvancedMarkerElement | null>(null)
  const [infoWindowShown, setInfoWindowShown] = useState(false)
  const [selectedStore, setSelectedStore] = useState<null | StoreLocator>(null)

  const onMarkerClick = useCallback(
    (
      store: StoreLocator,
      marker?: google.maps.marker.AdvancedMarkerElement,
    ) => {
      setSelectedId(store.id.toString())
      setSelectedStore(store)

      if (marker) {
        setSelectedMarker(marker)
      }

      if (store.id.toString() !== selectedId) {
        setInfoWindowShown(true)
      } else {
        setInfoWindowShown((isShown) => !isShown)
      }
    },
    [selectedId],
  )

  const onMapClick = useCallback(() => {
    setSelectedId(null)
    setSelectedMarker(null)
    setInfoWindowShown(false)
  }, [])

  const handleInfowindowCloseClick = useCallback(
    () => setInfoWindowShown(false),
    [],
  )

  if (isLoading) {
    return <div>Loading...</div>
  }

  return (
    <APIProvider apiKey={MAP_API_KEY}>
      <div className="grid gap-16 md:grid-cols-12">
        <div className="md:col-span-8">
          <Map
            className="aspect-square h-full w-full"
            defaultZoom={14}
            disableDefaultUI
            gestureHandling={'greedy'}
            id={'store-locator-map'}
            mapId={'b5387d230c6cf22f'}
            onClick={onMapClick}
          >
            {data?.map((store) => (
              <AdvancedMarkerWithRef
                className="custom-marker"
                key={store.id}
                onMarkerClick={(
                  marker: google.maps.marker.AdvancedMarkerElement,
                ) => onMarkerClick(store, marker)}
                position={{
                  lat: store.location.lat,
                  lng: store.location.lng,
                }}
              >
                <div className="h-14 w-14">
                  <MapSvg />
                </div>
              </AdvancedMarkerWithRef>
            ))}
            {infoWindowShown && selectedMarker && (
              <InfoWindow
                anchor={selectedMarker}
                onCloseClick={handleInfowindowCloseClick}
              >
                <h2 className="mb-2 text-lg font-semibold uppercase">
                  {selectedStore?.name}
                </h2>
                <p>{selectedStore?.content}</p>
                <Button
                  className="mt-4"
                  color="primary"
                  onPress={() =>
                    openDirections(selectedStore?.location.address || '')
                  }
                >
                  Directions
                </Button>
              </InfoWindow>
            )}
          </Map>
        </div>
        <div className="space-y-10 md:col-span-4">
          <StoreLocatorList
            selectedStore={selectedStore}
            setInfoWindowShown={setInfoWindowShown}
            setSelectedStore={setSelectedStore}
            stores={data!}
          />
        </div>
      </div>
    </APIProvider>
  )
}

export default StoreLocatorMap
